import React from "react";
import { t } from "@auto/services";

import EChart from "@src/reporting/components/EChart";
import ReportingWidget from "@src/reporting/components/ReportingWidget";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import { reportingResourceKey } from "@src/reporting/model/reportingFilters.mjs";
import { loadRegisteredVoterGeography } from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const capacityUse = (row) => {
  const registeredVoterCount = Number(row?.registeredVoterCount || 0);
  const capacity = Number(row?.capacity || 0);

  if (capacity <= 0) {
    return 0;
  }

  return registeredVoterCount * 100 / capacity;
};

const formatPercent = (value) =>
  `${Number(value || 0).toFixed(2)}%`;

const DepartmentCapacityUseWidget = ({
  filter,
  refreshToken = 0,
  compact = false,
}) => {
  const resource = useReportingResource(
    () => loadRegisteredVoterGeography(filter, "DEPARTMENT"),
    {
      key: reportingResourceKey("department-capacity", filter),
      refreshToken,
      initialData: [],
    },
  );

  const rows = resource.data || [];

  const displayRows = React.useMemo(
    () => [...rows].sort((a, b) => {
      const aLabel = String(a?.code || a?.name || "");
      const bLabel = String(b?.code || b?.name || "");

      return aLabel.localeCompare(
        bLabel,
        undefined,
        { numeric: true, sensitivity: "base" },
      );
    }),
    [rows],
  );

  const chartRows = React.useMemo(
    () => [...displayRows].reverse(),
    [displayRows],
  );

  const option = React.useMemo(() => ({
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => {
        const item = params?.[0]?.data;
        if (!item?.row) return "";

        return `${item.row.name || item.row.code}<br/>${t("reporting.capacityUtilization")}: ${formatPercent(item.value)}`;
      },
    },
    grid: {
      left: compact ? 125 : 170,
      right: 72,
      top: 14,
      bottom: 30,
    },
    xAxis: {
      type: "value",
      min: 0,
      max: (value) => Math.max(
        100,
        Math.ceil(Number(value.max || 0) / 10) * 10,
      ),
      axisLabel: {
        formatter: (value) => formatPercent(value),
      },
    },
    yAxis: {
      type: "category",
      data: chartRows.map(
        (row) => String(row?.name || row?.code || row?.gid || ""),
      ),
      axisLabel: {
        width: compact ? 112 : 156,
        overflow: "truncate",
      },
    },
    series: [{
      type: "bar",
      data: chartRows.map((row) => {
        const value = capacityUse(row);

        return {
          value,
          row,
          label: {
            show: true,
            position: "right",
            fontSize: 10,
            formatter: formatPercent(value),
          },
        };
      }),
      labelLayout: {
        hideOverlap: false,
      },
    }],
  }), [chartRows, compact]);

  return (
    <ReportingWidget
      title={t("reporting.capacityUtilizationByDepartment")}
      subtitle={t("reporting.capacityUtilizationByDepartmentSubtitle")}
      loading={resource.loading}
      error={resource.error}
      empty={resource.hasLoaded && rows.length === 0}
    >
      <EChart
        option={option}
        loading={resource.loading}
        className={compact ? styles.smallChart : styles.geographyChart}
      />
    </ReportingWidget>
  );
};

export default DepartmentCapacityUseWidget;